import posthog from 'posthog-js';

// Suppress informational logs in production
const debugLog = process.env.NODE_ENV === 'production' ? () => {} : console.log.bind(console);
const debugWarn = process.env.NODE_ENV === 'production' ? () => {} : console.warn.bind(console);

// PostHog configuration
// Uses /ingest reverse proxy (vercel.json rewrites) to avoid ad blockers
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || '/ingest';

let isInitialized = false;
let hasConsent = false;

// Initialize PostHog (call once at app startup)
export const initAnalytics = () => {
  if (!POSTHOG_KEY) {
    debugLog('PostHog key not configured - analytics disabled');
    return;
  }

  if (isInitialized) return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: 'https://us.posthog.com', // Keep toolbar/surveys on PostHog domain
    // Privacy-first defaults
    autocapture: false, // Don't auto-capture clicks/forms
    capture_pageview: false, // We'll capture manually
    capture_pageleave: false,
    disable_session_recording: true, // No session replay by default
    persistence: 'localStorage',
    // GDPR compliance
    opt_out_capturing_by_default: true, // Start opted out
    respect_dnt: true, // Respect Do Not Track
  });

  isInitialized = true;
  debugLog('Analytics initialized (opted out by default)');
};

// Set consent status (call when user consents/revokes)
export const setAnalyticsConsent = (consent) => {
  if (!isInitialized) return;

  hasConsent = consent;

  if (consent) {
    posthog.opt_in_capturing();
    debugLog('Analytics: User opted in');
  } else {
    posthog.opt_out_capturing();
    debugLog('Analytics: User opted out');
  }
};

// Check if analytics is enabled
export const isAnalyticsEnabled = () => {
  return isInitialized && hasConsent;
};

// Identify user (call after login, only if consented)
export const identifyUser = (userId, traits = {}) => {
  if (!isAnalyticsEnabled()) return;

  // Only send non-PII traits
  const safeTraits = {
    tier: traits.tier,
    has_home_city: !!traits.home_city,
    travel_pace: traits.travel_pace,
    default_budget: traits.default_budget,
    onboarding_completed: traits.onboarding_completed,
  };

  posthog.identify(userId, safeTraits);
};

// Reset user (call on logout)
export const resetUser = () => {
  if (!isInitialized) return;
  posthog.reset();
};

// ============================================
// ANONYMOUS EVENTS (always tracked if init)
// These don't require consent - no PII involved
// ============================================

export const trackAnonymous = (eventName, properties = {}) => {
  if (!isInitialized) return;

  // These are aggregate metrics only
  const safeEvents = [
    'app_loaded',
    'feature_used',
    'error_occurred',
  ];

  if (!safeEvents.includes(eventName)) {
    debugWarn(`Anonymous event "${eventName}" not in safelist`);
    return;
  }

  // Use a separate project/distinct_id for anonymous metrics if needed
  posthog.capture(eventName, {
    ...properties,
    $set_once: { first_seen: new Date().toISOString() },
  });
};

// ============================================
// USER EVENTS (require consent)
// ============================================

export const trackEvent = (eventName, properties = {}) => {
  if (!isAnalyticsEnabled()) return;

  posthog.capture(eventName, properties);
};

// ============================================
// PREDEFINED EVENTS
// ============================================

// Itinerary events
export const trackItineraryGenerated = (properties = {}) => {
  trackEvent('itinerary_generated', {
    destination_count: properties.destinationCount,
    trip_length_days: properties.tripLength,
    budget_tier: properties.budget,
    transport_mode: properties.transportMode,
    has_modifications: properties.hasModifications,
  });
};

export const trackItineraryModified = (properties = {}) => {
  trackEvent('itinerary_modified', {
    modification_type: properties.type, // 'add_day', 'remove_activity', 'change_hotel', etc.
    day_number: properties.dayNumber,
  });
};

export const trackItinerarySaved = (properties = {}) => {
  trackEvent('itinerary_saved', {
    destination: properties.destination,
    trip_length_days: properties.tripLength,
  });
};

// Search/Discovery events
export const trackDestinationSearched = (properties = {}) => {
  trackEvent('destination_searched', {
    query_type: properties.queryType, // 'specific', 'exploratory', 'constraint-based'
    has_constraints: properties.hasConstraints,
  });
};

// Feature usage
export const trackFeatureUsed = (featureName, properties = {}) => {
  trackEvent('feature_used', {
    feature: featureName,
    ...properties,
  });
};

// Errors (anonymous - no consent needed)
export const trackError = (errorType, properties = {}) => {
  if (!isInitialized) return;

  posthog.capture('error_occurred', {
    error_type: errorType,
    error_message: properties.message?.substring(0, 100), // Truncate
    page: window.location.pathname,
  });
};

// Page views (requires consent for full tracking)
export const trackPageView = (pageName) => {
  if (!isAnalyticsEnabled()) return;

  posthog.capture('$pageview', {
    page_name: pageName,
  });
};

// ============================================
// FEATURE FLAGS (PostHog built-in)
// ============================================

export const isFeatureEnabled = (flagName) => {
  if (!isInitialized) return false;
  return posthog.isFeatureEnabled(flagName);
};

export const getFeatureFlag = (flagName) => {
  if (!isInitialized) return null;
  return posthog.getFeatureFlag(flagName);
};

// ============================================
// A/B TESTING HELPERS
// ============================================

export const getExperimentVariant = (experimentName) => {
  if (!isInitialized) return 'control';
  return posthog.getFeatureFlag(experimentName) || 'control';
};

export default {
  initAnalytics,
  setAnalyticsConsent,
  isAnalyticsEnabled,
  identifyUser,
  resetUser,
  trackEvent,
  trackAnonymous,
  trackItineraryGenerated,
  trackItineraryModified,
  trackItinerarySaved,
  trackDestinationSearched,
  trackFeatureUsed,
  trackError,
  trackPageView,
  isFeatureEnabled,
  getFeatureFlag,
  getExperimentVariant,
};
